// 📄 services/credencialAccesoService.ts
// Obtener credenciales con aprobación e historial
// (docs/plans/plan-revelado-credenciales.md). Todo pasa por onehubApi
// (/v1/credential-access): las políticas, solicitudes, historial y permisos
// viven en colecciones que la web no puede leer directo.
import { getAuth } from 'firebase/auth';
import { IflowApiError } from './iflowService.ts';

export type TipoCredencial = 'cig' | 'sunat' | 'api' | 'vault';
export type ModoAprobacion = 'uno' | 'todos';

/** Qué credencial: CIG/SUNAT/API son del cliente; Vault, del proyecto. */
export interface CredencialRef {
  tipo: TipoCredencial;
  clienteId?: string;
  apiId?: string;
  projectId?: string;
  credId?: string;
}

export interface PersonaAcceso {
  uid: string;
  nombre: string;
  email: string;
}

export interface AccesoCredencial {
  credencial: CredencialRef;
  etiqueta: string;
  politica: {
    porDefecto: boolean;
    creador: (PersonaAcceso & { activo: boolean }) | null;
    aprobadores: (PersonaAcceso & { disponible: boolean })[];
    modo: ModoAprobacion;
    expuesta: (PersonaAcceso & { en: string | null }) | null;
  };
  elegibles: (PersonaAcceso & { esCliente: boolean; esGestorOrg: boolean })[];
  yo: {
    puedeRevelarDirecto: boolean;
    puedeSolicitar: boolean;
    puedeGestionarPolitica: boolean;
    puedeVerHistorial: boolean;
    solicitudVigente: SolicitudCredencial | null;
  };
}

export type EstadoSolicitud = 'pendiente' | 'aprobada' | 'rechazada' | 'cancelada' | 'expirada' | 'revelada';

export interface SolicitudCredencial {
  id: string;
  credencial: CredencialRef;
  etiqueta: string;
  orgId: string | null;
  solicitante: { uid: string; nombre: string };
  motivo: string;
  estado: EstadoSolicitud;
  modo: ModoAprobacion;
  aprobadores: string[];
  decisiones: { uid: string; nombre: string; decision: 'aprobar' | 'rechazar'; comentario: string; en: string | null }[];
  creadaEn: string | null;
  aprobadaEn: string | null;
  expiraEn: string | null;
  reveladaEn: string | null;
}

export interface CredencialRevelada {
  etiqueta: string;
  usuario: string;
  secretos: Record<string, string>;
  ocultarEnSegundos: number;
}

export interface EventoHistorial {
  id: string;
  tipoEvento: string;
  ubicacion: CredencialRef | { tipo: 'proyecto'; projectId: string };
  etiqueta: string;
  actorUid: string;
  actorNombre: string;
  solicitudId: string | null;
  detalle: Record<string, any>;
  ip: string;
  userAgent: string;
  en: string | null;
}

const pedir = async <T,>(metodo: string, ruta: string, body?: object): Promise<T> => {
  const user = getAuth().currentUser;
  if (!user) throw new Error('No se detectó una sesión activa. Por favor, reingresa al sistema.');
  const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/credential-access${ruta}`, {
    method: metodo,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  if (!res.ok) {
    const { code = 'ERROR', error, ...extra } = await res.json().catch(() => ({}));
    throw new IflowApiError(res.status, code, error || `Error ${res.status} al comunicarse con el servidor.`, extra);
  }
  return res.json();
};

// Contrato en inglés del backend (routes/v1/credencialAcceso.js).
const aContrato = (c: CredencialRef) => ({ type: c.tipo, clientId: c.clienteId, apiId: c.apiId, projectId: c.projectId, credentialId: c.credId });
const aQuery = (c: CredencialRef, extra: Record<string, string | number | undefined> = {}) => {
  const params = new URLSearchParams();
  Object.entries({ ...aContrato(c), ...extra }).forEach(([k, v]) => { if (v !== undefined && v !== '') params.set(k, String(v)); });
  return params.toString();
};

export const obtenerAcceso = (c: CredencialRef) => pedir<AccesoCredencial>('GET', `/access?${aQuery(c)}`);

export const guardarPolitica = (c: CredencialRef, aprobadores: string[], modo: ModoAprobacion) =>
  pedir<{ politicaId: string; aprobadores: string[]; modo: ModoAprobacion; solicitudesCanceladas: number }>(
    'PUT', '/policy', { credential: aContrato(c), approvers: aprobadores, mode: modo === 'todos' ? 'all' : 'any' }
  );

export const crearSolicitud = (c: CredencialRef, motivo: string) =>
  pedir<{ id: string; estado: EstadoSolicitud; aprobadores: string[]; modo: ModoAprobacion }>('POST', '/requests', { credential: aContrato(c), reason: motivo });

export const listarSolicitudes = (ambito: 'mias' | 'porAprobar') =>
  pedir<{ solicitudes: SolicitudCredencial[] }>('GET', `/requests?scope=${ambito === 'mias' ? 'mine' : 'to-approve'}`).then((r) => r.solicitudes);

export const decidirSolicitud = (id: string, decision: 'aprobar' | 'rechazar', comentario = '') =>
  pedir<{ id: string; estado: EstadoSolicitud }>('POST', `/requests/${encodeURIComponent(id)}/decision`, { decision: decision === 'aprobar' ? 'approve' : 'reject', comment: comentario });

export const cancelarSolicitud = (id: string) => pedir<{ id: string; estado: EstadoSolicitud }>('POST', `/requests/${encodeURIComponent(id)}/cancel`);

/** El backend exige una sesión reciente: reautenticar ANTES de llamar (ver ReautenticarModal). */
export const revelarConSolicitud = (id: string) => pedir<CredencialRevelada>('POST', `/requests/${encodeURIComponent(id)}/reveal`);
export const revelarPropia = (c: CredencialRef, motivo = '') => pedir<CredencialRevelada>('POST', '/reveal-own', { credential: aContrato(c), reason: motivo });

export const historialCredencial = (c: CredencialRef, despuesDe?: string) =>
  pedir<{ eventos: EventoHistorial[]; siguiente: string | null }>('GET', `/history?${aQuery(c, { after: despuesDe })}`);
export const historialOrganizacion = (orgId: string, despuesDe?: string) =>
  pedir<{ eventos: EventoHistorial[]; siguiente: string | null }>('GET', `/history/org/${encodeURIComponent(orgId)}${despuesDe ? `?after=${encodeURIComponent(despuesDe)}` : ''}`);

export const listarPermisosCredenciales = (projectId: string) =>
  pedir<{ miembros: { uid: string; otorgadoPor: string; en: string | null }[] }>('GET', `/permissions/${encodeURIComponent(projectId)}`).then((r) => r.miembros);
export const guardarPermisoCredenciales = (projectId: string, miembroUid: string, puedeObtener: boolean) =>
  pedir<{ projectId: string; miembroUid: string; puedeObtener: boolean }>(
    'PUT', `/permissions/${encodeURIComponent(projectId)}/${encodeURIComponent(miembroUid)}`, { canObtain: puedeObtener }
  );
